import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
    Box,
    TextField,
    Button,
    CircularProgress,
    Paper,
    Tooltip,
    IconButton,
    Snackbar,
    Alert,
    Typography,
    Autocomplete,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useAuth } from '../context/AuthContext';
import { getCurrentFYear } from '../utils/financialYear';

export default function GroupWiseDiscMaster({ partyId, partyName, connectionString, tempRows, setTempRows, isFinalSubmitting = false }) {
    // When tempRows/setTempRows are supplied (inline usage from ClientMaster),
    // "Add Group" stages rows locally instead of hitting the database — the
    // actual insert happens later from the parent's Submit/Update button, same
    // as ClientConsigneeMaster's / PartyWiseItemDialog's temp-row pattern.
    const useTempRows = typeof setTempRows === 'function';
    const { user, selectedCompany } = useAuth();
    const API_BASE = `${connectionString}/api`;
    const companyId = selectedCompany?.CompId;
    const AppRegID = selectedCompany?.AppRegID;
    const GCompID = selectedCompany?.GCompId;
    const fYear = getCurrentFYear();
    const userId = user?.UserId;
    const BranchId = selectedCompany?.BranchId;

    // States
    const [gridRows, setGridRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Search & Form Fields
    const [groups, setGroups] = useState([]);
    const [groupOpen, setGroupOpen] = useState(false);
    const [groupInputValue, setGroupInputValue] = useState('');
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [disc, setDisc] = useState('');
    const [editingRowId, setEditingRowId] = useState(null);

    // Fetch product groups
    const fetchGroups = useCallback(async () => {
        if (!companyId) return;
        try {
            const { data } = await axios.get(`${API_BASE}/product-get-data/api/item-group?companyId=${companyId}`);
            console.log(data);
            if (Array.isArray(data)) {
                setGroups(data);
            }
        } catch (error) {
            console.error('Error fetching groups:', error);
            showSnackbar('Failed to load groups', 'error');
        }
    }, [API_BASE, companyId]);

    useEffect(() => {
        fetchGroups();
    }, [fetchGroups]);

    // Fetch group-wise discounts when party changes
    useEffect(() => {
        if (partyId) {
            fetchGroupWiseItems();
        }
    }, [partyId]);

    const fetchGroupWiseItems = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await axios.get(`${API_BASE}/groupwise/getByParty/${partyId}`, {
                params: { companyId, AppRegID },
            });
            if (Array.isArray(data)) {
                const transformedRows = data.map((item, index) => ({
                    id: item.GroupWiseRateID || index,
                    GroupWiseRateID: item.GroupWiseRateID,
                    PartyId: item.PartyId,
                    ProdGRPID: item.ProdGRPID,
                    SrNo: item.SrNo,
                    GroupName: item.GroupName,
                    Disc: Number(item.Disc || 0),
                }));
                setGridRows(transformedRows);
            }
        } catch (error) {
            console.error('Error fetching group-wise discounts:', error);
            showSnackbar('Failed to load group discounts', 'error');
        } finally {
            setLoading(false);
        }
    }, [API_BASE, partyId, companyId, AppRegID]);

    // Local filter over the already-fetched groups list (no per-keystroke API call)
    const groupOptions = groupInputValue
        ? groups.filter(g => (g.GroupName || '').toLowerCase().includes(groupInputValue.toLowerCase()))
        : groups;

    const handleGroupSelect = (group) => {
        setSelectedGroup(group);
        setEditingRowId(null);
    };

    const handleEditRow = (row) => {
        setSelectedGroup({ GroupName: row.GroupName, ProdGRPID: row.ProdGRPID });
        setDisc(row.Disc.toString());
        setEditingRowId(row.id);
    };

    const handleAddGroup = () => {
        if (isFinalSubmitting) return;

        if (!selectedGroup) {
            showSnackbar('Please select a group', 'warning');
            return;
        }

        if (disc === '' || disc === null || disc === undefined) {
            showSnackbar('Please enter discount', 'warning');
            return;
        }

        const newRow = {
            id: editingRowId || (useTempRows ? `temp-${Date.now()}` : Date.now()),
            isTemp: useTempRows ? true : undefined,
            GroupWiseRateID: editingRowId ? [...(tempRows || []), ...gridRows].find(r => r.id === editingRowId)?.GroupWiseRateID : null,
            PartyId: partyId,
            ProdGRPID: selectedGroup.ProdGRPID,
            GroupName: selectedGroup.GroupName,
            Disc: Number(disc || 0),
        };

        if (useTempRows) {
            // Stage locally — the actual insert/update happens later from the parent's
            // Submit/Update button (same pattern as Consignee Management / Partywise Item
            // Rate). This applies whether the row is brand-new OR an edit of an already-
            // persisted DB row — an edited DB row is moved out of gridRows and into
            // tempRows (carrying its real GroupWiseRateID) so the parent's submit sends
            // it as an UPDATE, not a silently-dropped no-op.
            const isEditingExistingDbRow = editingRowId && !(tempRows || []).some(r => r.id === editingRowId);
            if (isEditingExistingDbRow) {
                setGridRows(prev => prev.filter(r => r.id !== editingRowId));
                setTempRows(prev => [...prev, newRow]);
            } else if (editingRowId) {
                setTempRows(prev => prev.map(r => (r.id === editingRowId ? newRow : r)));
            } else {
                setTempRows(prev => [...prev, newRow]);
            }
        } else if (editingRowId) {
            setGridRows(prev => prev.map(r => (r.id === editingRowId ? newRow : r)));
        } else {
            setGridRows(prev => [...prev, newRow]);
        }

        setEditingRowId(null);
        resetFormFields();
        showSnackbar(editingRowId ? 'Group updated' : 'Group added successfully', 'success');
    };

    const handleDeleteRow = (rowId) => {
        // A row can live in either array — a still-unsaved temp row, or an
        // already-persisted DB row loaded into gridRows — so remove it from
        // whichever one actually has it instead of assuming based on useTempRows.
        if (useTempRows && (tempRows || []).some(r => r.id === rowId)) {
            setTempRows(prev => prev.filter(r => r.id !== rowId));
        } else {
            setGridRows(prev => prev.filter(r => r.id !== rowId));
        }
        showSnackbar('Group removed', 'success');
    };

    const handleSave = async () => {
        if (gridRows.length === 0) {
            showSnackbar('Please add at least one group', 'warning');
            return;
        }

        try {
            setSaving(true);
            const payload = {
                partyId,
                items: gridRows.map(row => ({
                    GroupWiseRateID: row.GroupWiseRateID || null,
                    ProdGRPID: row.ProdGRPID,
                    Disc: row.Disc,
                })),
                companyId,
                AppRegID,
                GCompID,
                userId,
                BranchId,
                fYear,
            };

            await axios.post(`${API_BASE}/groupwise/saveRates`, payload);
            showSnackbar('Group-wise discounts saved successfully', 'success');
            fetchGroupWiseItems();
        } catch (error) {
            console.error('Error saving group-wise discounts:', error);
            showSnackbar('Failed to save group-wise discounts', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteDb = async (rowId) => {
        const row = [...(tempRows || []), ...gridRows].find(r => r.id === rowId);
        if (!row?.GroupWiseRateID) {
            handleDeleteRow(rowId);
            return;
        }

        try {
            await axios.delete(`${API_BASE}/groupwise/deleteRate/${row.GroupWiseRateID}`, {
                params: { companyId, AppRegID },
            });
            handleDeleteRow(rowId);
            showSnackbar('Group deleted successfully', 'success');
        } catch (error) {
            console.error('Error deleting group discount:', error);
            showSnackbar('Failed to delete group discount', 'error');
        }
    };

    const resetFormFields = () => {
        setGroupInputValue('');
        setDisc('');
        setSelectedGroup(null);
    };

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleSnackbarClose = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const columns = [
        { field: 'GroupName', headerName: 'Group Name', width: 220, flex: 1 },
        {
            field: 'Disc',
            headerName: 'Disc (%)',
            width: 100,
            align: 'right',
            renderCell: params => Number(params.row.Disc).toFixed(2),
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 100,
            sortable: false,
            renderCell: params => (
                <Box sx={{ display: 'flex', gap: '4px' }}>
                    <Tooltip title="Edit">
                        <IconButton
                            size="small"
                            onClick={() => handleEditRow(params.row)}
                            sx={{ color: 'primary.main' }}
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <IconButton
                            size="small"
                            onClick={() => handleDeleteDb(params.row.id)}
                            sx={{ color: 'error.main' }}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
        },
    ];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography fontWeight={600} sx={{ fontSize: '0.95rem' }}>
                Groupwise Disc - {partyName}
            </Typography>

            {/* Search & Form Section */}
            <Paper sx={{ p: 1.5, backgroundColor: '#f5f5f5' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5 }}>
                        <Autocomplete
                            size="small"
                            open={groupOpen}
                            onOpen={() => setGroupOpen(true)}
                            onClose={() => setGroupOpen(false)}
                            value={selectedGroup}
                            onChange={(e, newValue) => handleGroupSelect(newValue)}
                            inputValue={groupInputValue}
                            onInputChange={(e, newInputValue) => setGroupInputValue(newInputValue || '')}
                            options={groupOptions}
                            getOptionLabel={(option) => option?.GroupName || ''}
                            isOptionEqualToValue={(option, value) => option.ProdGRPID === value.ProdGRPID}
                            noOptionsText="No groups found"
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Group Name"
                                />
                            )}
                        />
                        <TextField
                            label="Discount (%)"
                            type="number"
                            value={disc}
                            onChange={e => setDisc(e.target.value)}
                            size="small"
                            inputProps={{ step: '0.01' }}
                        />
                    </Box>

                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleAddGroup}
                        fullWidth
                        size="small"
                    >
                        {editingRowId ? 'Update Group' : 'Add Group'}
                    </Button>
                </Box>
            </Paper>

            {/* DataGrid */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Paper sx={{ height: 280 }}>
                    <DataGrid
                        rows={useTempRows ? [...tempRows, ...gridRows] : gridRows}
                        columns={columns}
                        pageSizeOptions={[5, 10, 25]}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 5 } },
                        }}
                        density="compact"
                        sx={{ border: 'none', fontSize: '0.8rem' }}
                    />
                </Paper>
            )}

            {/* When staging into tempRows, there's nothing to separately save here —
                rows are committed to the database from the parent's own Submit/Update
                button (see ClientMaster's submitAllGroupWiseItems), same as Consignee
                and Partywise Item Rate. */}
            {!useTempRows && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        size="small"
                        disabled={gridRows.length === 0 || saving}
                    >
                        {saving ? <CircularProgress size={20} /> : 'Save'}
                    </Button>
                </Box>
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
